import type { ConsentPreferences } from '../privacy/ConsentService';

declare global {
  interface Window {
    dataLayer?: unknown[];
    ym?: YandexMetrikaCounter;
  }
}

type YandexMetrikaCounter = {
  (counterId: number, method: 'init', options: YandexMetrikaInitOptions): void;
  (counterId: number, method: 'hit', url: string, options?: YandexMetrikaHitOptions): void;
  (counterId: number, method: 'reachGoal', goal: string, params?: Record<string, unknown>): void;
  (counterId: number, method: 'disableCookies'): void;
  a?: unknown[];
  l?: number;
};

interface YandexMetrikaInitOptions {
  accurateTrackBounce: boolean;
  clickmap: boolean;
  defer: boolean;
  ecommerce: string;
  referrer: string;
  ssr: boolean;
  trackLinks: boolean;
  url: string;
  webvisor: boolean;
}

interface YandexMetrikaHitOptions {
  referer?: string;
  title?: string;
}

export class YandexMetrikaService {
  private readonly defaultCounterId = 109498994;
  private readonly counterId = this.getCounterId();
  private readonly scriptId = 'yandex-metrika-tag';
  private initialized = false;
  private analyticsAllowed = false;
  private lastTrackedUrl: string | null = null;

  public sync(preferences: ConsentPreferences | null, url: string, title: string): void {
    const counterId = this.counterId;

    if (!counterId) {
      return;
    }

    this.analyticsAllowed = Boolean(preferences?.analytics);

    if (!preferences?.analytics) {
      this.disableCookies(counterId);
      return;
    }

    this.initialize(counterId, url);
    this.trackPageView(counterId, url, title);
  }

  public reachGoal(goal: string, params?: Record<string, unknown>): void {
    if (!this.counterId || !this.initialized || !this.analyticsAllowed) {
      return;
    }

    window.ym?.(this.counterId, 'reachGoal', goal, params);
  }

  private initialize(counterId: number, currentUrl: string): void {
    if (this.initialized) {
      return;
    }

    this.ensureCounterFunction();
    this.ensureDataLayer();
    this.ensureScript(counterId);

    window.ym?.(counterId, 'init', {
      accurateTrackBounce: true,
      clickmap: true,
      defer: true,
      ecommerce: 'dataLayer',
      referrer: document.referrer,
      ssr: true,
      trackLinks: true,
      url: currentUrl,
      webvisor: true,
    });

    this.initialized = true;
    this.lastTrackedUrl = currentUrl;
  }

  private trackPageView(counterId: number, url: string, title: string): void {
    if (url === this.lastTrackedUrl) {
      return;
    }

    window.ym?.(counterId, 'hit', url, {
      title,
      referer: this.lastTrackedUrl ?? undefined,
    });
    this.lastTrackedUrl = url;
  }

  private disableCookies(counterId: number): void {
    if (!this.initialized) {
      return;
    }

    window.ym?.(counterId, 'disableCookies');
  }

  private ensureScript(counterId: number): void {
    if (document.getElementById(this.scriptId)) {
      return;
    }

    const script = document.createElement('script');
    script.id = this.scriptId;
    script.async = true;
    script.src = `https://mc.yandex.ru/metrika/tag.js?id=${counterId}`;
    document.head.append(script);
  }

  private ensureDataLayer(): void {
    window.dataLayer = window.dataLayer ?? [];
  }

  private ensureCounterFunction(): void {
    if (window.ym) {
      return;
    }

    window.ym = ((...args: Parameters<YandexMetrikaCounter>) => {
      window.ym!.a = window.ym!.a ?? [];
      window.ym!.a.push(args);
    }) as YandexMetrikaCounter;
    window.ym.l = Date.now();
  }

  private getCounterId(): number | null {
    const rawValue = import.meta.env.VITE_YANDEX_METRIKA_ID;
    const counterId = Number(rawValue);

    return Number.isInteger(counterId) && counterId > 0 ? counterId : this.defaultCounterId;
  }
}
